import type { IntlConfig } from 'react-intl';

/**
 * Alias for react-intl's `IntlConfig.messages` type.
 *
 * See react-intl's {@link https://formatjs.io/docs/react-intl/components/#intlprovider|IntlProvider} documentation.
 */
export type Messages = IntlConfig['messages'];

/**
 * An object containing translations for multiple locales.
 *
 * The object keys are locale keys (ex: 'xx-XX').
 * The object values are messages for that locale. This messages object has react-intl's `IntlConfig.messages` type.
 *
 * AllMessages is equivalent to the type of the `messages` prop in both Adobe-Intl-React's I18nProvider and react-aria's I18nProvider, which accept translations for all locales in a single object. It is NOT equivalent to react-intl's, which accepts translations for only a single locale.
 *
 * Adobe-Intl-React outputs translation files that, when imported, match AllMessages.
 * For example, inside a localized component, `messages` here will have the type AllMessages:
 * `import * as messages from './__localization__';`
 */
export type AllMessages = Record<string, Messages>;

export type GetMessages = (locale: string) => Promise<{ default: Messages }>;

/**
 * The minimum set of internationalization config that contains messages for all supported locales.
 */
export interface IntlConfigWithAllMessages {
  /** Default locale */
  defaultLocale?: string | null;
  /** Locale */
  locale?: string | null;
  /** Messages for all supported locales */
  messages?: AllMessages;
}

/**
 * The minimum set of internationalization config that contains messages for a single locale.
 */
export interface IntlConfigWithMessagesForLocale {
  /** Default locale */
  defaultLocale?: string | null;
  /** Locale */
  locale?: string | null;
  /** Messages for a single locale */
  messages?: Messages;
}

/**
 * Props for IntlProvider's internal react-intl provider.
 *
 * Same as {@link IntlConfigWithMessagesForLocale|IntlConfigWithMessagesForLocale}, but all props are required.
 */
export interface ReactIntlProviderProps {
  /** Default locale */
  defaultLocale: string;
  /** Locale */
  locale: string;
  /** Messages for a single locale */
  messages: Messages;
}

export interface LocaleOptions {
  locale?: string | null;
  parentLocale?: string | null;
  defaultLocale?: string | null;
  parentDefaultLocale?: string | null;
}

export enum TranslationStringStatus {
  AVAILABLE,
  FETCHING,
  ERRORED,
  TIMED_OUT,
}
